import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { FileText, ShieldCheck, Zap, Pencil } from "lucide-react";
import { useDispatch } from "react-redux"; // <-- Added
import { SET_SalesOrderConfig } from "../../../redux/IchthusSlice"; // <-- Added
import Loader from "../../loader/Loader";
import AddSalesOrderSettings from "./AddSalesOrderSettings";
import { domain } from "../../../security";

const AllSalesOrderSettings = () => {
  const dispatch = useDispatch(); // <-- Added
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // This holds the single global setting (boolean)
  const [withSalesQuotation, setWithSalesQuotation] = useState(true);

  const fetchGlobalSetting = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${domain}/api/SalesOrderSettings`);
      setWithSalesQuotation(res.data.withSalesQuotation);

      // --> ADD THIS: Ensure Redux has the latest initial value from DB
      dispatch(SET_SalesOrderConfig(res.data.withSalesQuotation));
    } catch (err) {
      toast.error("Failed to load configuration.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGlobalSetting();
  }, []);

  if (isLoading) return <Loader />;

  return (
    <div className="min-h-screen bg-slate-100/40 pb-12 font-sans">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-white to-slate-50/50 border-b border-slate-100 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-light text-slate-900 flex items-center gap-2.5 tracking-tight">
                <div className="p-2.5 rounded-lg bg-indigo-100/60 text-indigo-600">
                  <FileText size={20} />
                </div>
                Global Sales Order Configuration
              </h1>
              <p className="text-sm text-slate-500 mt-2">
                View or change the system-wide rule for Sales Quotations.
              </p>
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={() => setIsModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-6 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all active:scale-95 duration-200"
              >
                <Pencil size={18} />
                Edit Configuration
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Content (Displaying Saved Data) ── */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
              Current Active Rule
            </h3>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 bg-green-100 text-green-700 rounded-full border border-green-200">
              Live
            </span>
          </div>

          <div className="p-8 flex flex-col items-center justify-center text-center">
            {withSalesQuotation ? (
              <>
                <div className="w-20 h-20 rounded-full bg-emerald-50 border-4 border-emerald-100 flex items-center justify-center mb-4">
                  <ShieldCheck size={40} className="text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-emerald-700 mb-2">
                  With Sales Quotation
                </h2>
                <p className="text-slate-500 max-w-md">
                  The system currently requires a valid Sales Quotation before
                  any user can create a Sales Order.
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-blue-50 border-4 border-blue-100 flex items-center justify-center mb-4">
                  <Zap size={40} className="text-blue-500" />
                </div>
                <h2 className="text-2xl font-bold text-blue-700 mb-2">
                  Without Sales Quotation
                </h2>
                <p className="text-slate-500 max-w-md">
                  The system currently allows users to directly create Sales
                  Orders without needing a prior Quotation.
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Edit Modal ── */}
      {isModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <AddSalesOrderSettings
              currentSetting={withSalesQuotation}
              onClose={() => setIsModalVisible(false)}
              refreshData={fetchGlobalSetting}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AllSalesOrderSettings;
